import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './CredentialsSettingsPage.module.css';

const CredentialsSettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('email');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    current_email: '',
    email: '',
    email_confirmation: '',
    current_password_email: '',
    password: '',
    password_confirmation: '',
    current_password_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadCurrentUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/user`);
        const user = res?.data || {};
        if (!mounted) return;

        setForm((prev) => ({
          ...prev,
          name: user.name || '',
          phone: user.phone || '',
          current_email: user.email || '',
        }));
        setUserRole(user.role || '');
      } catch {
        // 読み込み失敗時は既存入力のまま継続する
      }
    };

    loadCurrentUser();
    return () => {
      mounted = false;
    };
  }, []);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const finishWithRelogin = (message) => {
    setSuccess(message || '更新しました。再度ログインしてください。');
    setTimeout(() => navigate('/login', { replace: true }), 1200);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const nextEmail = form.email.trim();
      const confirmEmail = form.email_confirmation.trim();
      if (!nextEmail) {
        setError('新しいメールアドレスを入力してください。');
        return;
      }
      if (nextEmail !== confirmEmail) {
        setError('新しいメールアドレス（確認）が一致しません。');
        return;
      }

      const payload = {
        email: nextEmail,
        current_password: form.current_password_email,
      };

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/credentials`, payload);
      finishWithRelogin(res?.data?.message);
    } catch (err) {
      setError(err?.response?.data?.message || '更新に失敗しました。入力内容を確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!form.password) {
        setError('新しいパスワードを入力してください。');
        return;
      }

      if (form.password.length < 8) {
        setError('パスワードは8文字以上で入力してください。');
        return;
      }

      const payload = {
        password: form.password,
        password_confirmation: form.password_confirmation,
        current_password: form.current_password_password,
      };

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/credentials`, payload);
      finishWithRelogin(res?.data?.message);
    } catch (err) {
      setError(err?.response?.data?.message || '更新に失敗しました。入力内容を確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const handleOtherSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const nextName = form.name.trim();
      if (!nextName) {
        setError('名前（代表者）を入力してください。');
        return;
      }

      if (!form.current_email) {
        setError('プロフィール情報の取得に失敗しました。ページを再読み込みしてください。');
        return;
      }

      const payload = {
        name: nextName,
        email: form.current_email,
      };
      if (userRole !== 'buyer') {
        payload.phone = form.phone.trim() || null;
      }

      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/profile`, payload);
      setSuccess(res?.data?.message || '登録情報を更新しました。');
    } catch (err) {
      setError(err?.response?.data?.message || '更新に失敗しました。入力内容を確認してください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>登録情報変更</h1>
      <p className={styles.desc}>変更したい項目をタブで選択してください。</p>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'email' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('email')}
        >
          メールアドレス変更
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'password' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('password')}
        >
          パスワード変更
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'other' ? styles.tabActive : ''}`}
          type="button"
          onClick={() => switchTab('other')}
        >
          その他
        </button>
      </div>

      {activeTab === 'email' && (
        <form className={styles.form} onSubmit={handleEmailSubmit}>
          <input
            type="email"
            name="username"
            value={form.current_email}
            autoComplete="username"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className={styles.hiddenAutofillField}
          />

          <label className={styles.label}>新しいメールアドレス</label>
          <input
            className={styles.input}
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="new-email@example.com"
          />

          <label className={styles.label}>新しいメールアドレス（確認）</label>
          <input
            className={styles.input}
            type="email"
            name="email_confirmation"
            value={form.email_confirmation}
            onChange={handleChange}
            required
            autoComplete="email"
            placeholder="new-email@example.com"
          />

          <label className={styles.label}>現在のパスワード（必須）</label>
          <input
            className={styles.input}
            type="password"
            name="current_password_email"
            value={form.current_password_email}
            onChange={handleChange}
            required
            autoComplete="current-password"
            placeholder="現在のパスワード"
          />

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? '更新中...' : 'メールアドレスを更新'}
          </button>
        </form>
      )}

      {activeTab === 'password' && (
        <form className={styles.form} onSubmit={handlePasswordSubmit}>
          <input
            type="email"
            name="username"
            value={form.current_email}
            autoComplete="username"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className={styles.hiddenAutofillField}
          />

          <label className={styles.label}>新しいパスワード</label>
          <input
            className={styles.input}
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            placeholder="8文字以上"
          />

          <label className={styles.label}>新しいパスワード（確認）</label>
          <input
            className={styles.input}
            type="password"
            name="password_confirmation"
            value={form.password_confirmation}
            onChange={handleChange}
            required
            autoComplete="new-password"
            placeholder="確認用"
          />

          <label className={styles.label}>現在のパスワード（必須）</label>
          <input
            className={styles.input}
            type="password"
            name="current_password_password"
            value={form.current_password_password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            placeholder="現在のパスワード"
          />

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? '更新中...' : 'パスワードを更新'}
          </button>
        </form>
      )}

      {activeTab === 'other' && (
        <form className={styles.form} onSubmit={handleOtherSubmit}>
          <label className={styles.label}>{userRole === 'buyer' ? '名前' : '名前（代表者）※本名'}</label>
          <input
            className={styles.input}
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="山田 太郎"
          />

          {userRole !== 'buyer' && (
            <>
              <label className={styles.label}>電話番号</label>
              <input
                className={styles.input}
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="09012345678"
              />
            </>
          )}

          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? '更新中...' : 'その他情報を更新'}
          </button>
        </form>
      )}
    </div>
  );
};

export default CredentialsSettingsPage;
